import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_ORDER_SERVICE } from '@/api/constant';
import Breadcrumb from '@/components/common/Breadcrumb';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import { showToastNotification, ThrowDateTimeError } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import ErrorMessage from '../../../components/common/ErrorMessage';
import { errorTypes } from '@/utility/validation';
import BtnLoader from '@/components/UI/loaders/BtnLoader';
import CategoryTable from '@/components/category_brands/CategoryTable';
import { ICategoryData } from '@/interfaces/categoryResponse.interface';
import BrandTable from '@/components/category_brands/BrandTable';
import { IBrandsResponse } from '@/interfaces/brandsResponse.interface';

interface CouponProps {
  id?: string;
  name: string;
  code: string;
  type: string;
  description: string;
  discountValue: number;
  maxDiscount: number;
  startDate: string;
  expirationDate: string;
  minimumValue: number;
  maxTotalUsage: number | null;
  maxUsagePerUser: number | null;
  maxUsagePerDay: number | null;
  maxUsagePerUserPerDay: number | null;
  brandIds: string[] | null;
  categoryIds: string[] | null;
  minimumValueAppliesTo: 'cart_total' | 'eligible_items';
}

const AddProductCoupon = () => {
  const navigate = useNavigate();
  const { code } = useParams();
  const couponCode = code;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dateError, setDateError] = useState<string | null>(null);
  const [selectedData, setSelectedData] = useState<
    IBrandsResponse[] | ICategoryData[]
  >([]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    control,
    formState: { errors },
  } = useForm<CouponProps>({
    mode: 'onChange',
    defaultValues: {
      id: '',
      name: '',
      code: '',
      type: '',
      description: '',
      maxDiscount: 0,
      startDate: '',
      expirationDate: '',
      minimumValue: 0,
      minimumValueAppliesTo: 'cart_total',
      brandIds: [],
      categoryIds: [],
      maxTotalUsage: 1,
      maxUsagePerDay: 1,
      maxUsagePerUser: 1,
      maxUsagePerUserPerDay: 1,
    },
  });
  const id = watch('id');
  const [StartDate, endDate, maxDiscount] = [
    watch('startDate'),
    watch('expirationDate'),
    watch('maxDiscount'),
  ];
  const codeText = watch('code');
  const brandsIds = getValues('brandIds');
  const categoryIds = getValues('categoryIds');

  const [redirection, setRedirection] = useState<string>(
    brandsIds && brandsIds.length > 0
      ? 'brand'
      : categoryIds && categoryIds.length > 0
        ? 'category'
        : '',
  );

  const unlimitedHandler = (field: keyof CouponProps, checked: boolean) => {
    setValue(field, checked ? null : 1);
  };

  useEffect(() => {
    if (codeText && /\s/.test(codeText)) {
      setError('code', {
        type: 'manual',
        message: 'Coupon code cannot contain spaces.',
      });
    } else {
      clearErrors('code');
    }
  }, [codeText, setError, clearErrors]);

  useEffect(() => {
    if (!StartDate || !endDate) {
      setDateError('Start Date and end date are required');
      return;
    }
    const start = new Date(StartDate);
    const end = new Date(endDate);
    setDateError(ThrowDateTimeError(start, end));
  }, [StartDate, endDate]);

  const fetchCouponInfo = async () => {
    try {
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.coupons}/${couponCode}`;
      const res = await callApi(PATH, { method: 'GET' });
      reset({ ...res });

      if (res.brandIds?.length) {
        setRedirection('brand');
      } else if (res.categoryIds?.length) {
        setRedirection('category');
      } else {
        setRedirection('');
      }
    } catch (error) {
      console.log('error is', error);
    }
  };

  useEffect(() => {
    if (couponCode) fetchCouponInfo();
  }, [couponCode]);

  const onSubmit = async (data: CouponProps) => {
    if (dateError) {
      showToastNotification('danger', dateError);
      return;
    }
    setIsLoading(true);

    try {
      let PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.coupons}`;
      const payload = {
        ...data,
        categoryIds:
          redirection === 'category'
            ? Array.from(
                new Set([
                  ...(data.categoryIds || []),
                  ...selectedData.map((i) => i.id),
                ]),
              )
            : null,

        brandIds:
          redirection === 'brand'
            ? Array.from(
                new Set([
                  ...(data.brandIds || []),
                  ...selectedData.map((i) => i.id),
                ]),
              )
            : null,
      };
      if (couponCode) PATH = `${PATH}/${id}`;
      await callApi(PATH, {
        method: couponCode ? 'PUT' : 'POST',
        data: payload,
      });
      setIsLoading(false);
      navigate(-1);
    } catch (error: unknown) {
      setIsLoading(false);
      ThrowError(error);
    }
  };

  const discountType = watch('type');

  const breadcrumb = [
    { label: 'Coupons', href: RoutesLink.coupons },
    { label: couponCode ? 'Edit Coupon' : 'Add Coupon' },
  ];

  return (
    <Layout>
      <Breadcrumb items={breadcrumb} />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Discount Code */}
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <FormField
                label="Title"
                type="text"
                placeholder="eg: special code for eid"
                {...register('name', {
                  ...errorTypes.required('name'),
                  minLength: {
                    value: 4,
                    message: 'Coupon name must be at least 4 characters long.',
                  },
                })}
              />
              <ErrorMessage message={errors.name?.message} />
            </div>
            <div>
              <Controller
                name="code"
                control={control}
                defaultValue={code?.toUpperCase() || ''}
                rules={{
                  required: 'Coupon code is required',
                  minLength: { value: 4, message: 'At least 4 characters' },
                  pattern: {
                    value: /^[A-Za-z0-9&]+$/,
                    message: 'Coupon code can only contain letters and numbers',
                  },
                }}
                render={({ field }) => (
                  <FormField
                    label="Coupon Code"
                    type="text"
                    placeholder="Enter coupon code"
                    disabled={!!code}
                    {...field}
                    value={field.value?.toUpperCase() || ''}
                    onChange={(e) =>
                      field.onChange(e.target.value.toUpperCase())
                    }
                  />
                )}
              />
              <ErrorMessage message={errors.code?.message} />
              <p className="text-xs text-gray-500 mt-2">
                Customer have to enter this coupon at checkout
              </p>
              {couponCode && (
                <ErrorMessage
                  message="You can not edit the coupon code"
                  className="text-yellow-500! text-xs"
                />
              )}
            </div>
          </div>
          <div className="mt-3">
            <FormField
              label="Description"
              type="textarea"
              placeholder="Enter description for code"
              rows={5}
              {...register('description', {
                ...errorTypes.required('description'),
              })}
            />
            <ErrorMessage message={errors.description?.message} />
          </div>
        </Card>
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormField
                label="Coupon Type"
                type="select"
                placeholder="All Products"
                value={redirection}
                options={[
                  { value: 'brand', label: 'Brand' },
                  { value: 'category', label: 'Category' },
                ]}
                onChange={(v) => setRedirection(v.target.value)}
              />
            </div>
            <div>
              <FormField
                label="Min Value Apply To ?"
                type="select"
                options={[
                  { value: 'cart_total', label: 'On Cart Total' },
                  { value: 'eligible_items', label: 'On Eligible Items' },
                ]}
                {...register('minimumValueAppliesTo', {
                  ...errorTypes.required('Apply Value'),
                })}
              />
              <ErrorMessage message={errors.type?.message} />
            </div>
          </div>
        </Card>
        {redirection === 'brand' && (
          <BrandTable
            selectionMode="multiple"
            initialSelectedIds={brandsIds ? brandsIds : undefined}
            onSelectBrand={setSelectedData}
          />
        )}

        {redirection === 'category' && (
          <CategoryTable
            selectionMode="multiple"
            initialSelectedIds={categoryIds ? categoryIds : undefined}
            onSelectCtegory={setSelectedData}
          />
        )}
        {/* Discount Value */}
        <Card>
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Discount Value</h2>
            {/* Discount Type + Value */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
              <div>
                <FormField
                  label="Discount Type"
                  type="select"
                  options={[
                    { value: 'percentage', label: 'Percentage' },
                    { value: 'flat', label: 'Fixed Amount' },
                  ]}
                  {...register('type', { ...errorTypes.required('type') })}
                />
                <ErrorMessage message={errors.type?.message} />
              </div>
              <div>
                <FormField
                  label="Value"
                  type="number"
                  placeholder="20"
                  suffix={discountType === 'percentage' ? '%' : undefined}
                  prefix={discountType === 'flat' ? 'BD' : undefined}
                  min={1}
                  onWheel={(e) => e.currentTarget.blur()}
                  step="0.01"
                  {...register('discountValue', {
                    valueAsNumber: true,
                    ...errorTypes.required('discount value'),
                  })}
                />
                <ErrorMessage message={errors.discountValue?.message} />
              </div>
            </div>
            {/* Discount Type + Value */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
              <FormField
                label="Max Discount"
                type="number"
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="Enter Max Discount"
                min={1}
                {...register('maxDiscount', { valueAsNumber: true })}
              />

              <div>
                <FormField
                  label="Min Purchase Value"
                  type="number"
                  placeholder="20"
                  onWheel={(e) => e.currentTarget.blur()}
                  prefix="BD"
                  min={0.001}
                  step="0.001"
                  {...register('minimumValue', {
                    valueAsNumber: true,
                    validate: (value) =>
                      value <= maxDiscount ||
                      'Min purchase value cannot be more than max discount',
                  })}
                />
                <ErrorMessage message={errors.minimumValue?.message} />
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <h2 className="text-lg font-semibold mb-4">Usage Limits</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            {[
              ['maxTotalUsage', 'Max Total Usage'],
              ['maxUsagePerUser', 'Max Usage Per User'],
              ['maxUsagePerDay', 'Max Usage Per Day'],
              ['maxUsagePerUserPerDay', 'Max Usage Per User Per Day'],
            ].map(([key, label]) => {
              const field = key as keyof CouponProps;
              const value = watch(field);

              return (
                <div key={key} className="space-y-2">
                  <FormField
                    label={label}
                    placeholder={label}
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    disabled={value === null}
                    {...register(field, { valueAsNumber: true })}
                    min={0}
                  />

                  <FormField
                    type="checkbox"
                    label="Unlimited"
                    checked={value === null}
                    onChange={(e) =>
                      unlimitedHandler(
                        field,
                        (e.target as HTMLInputElement).checked,
                      )
                    }
                  />
                </div>
              );
            })}
          </div>
        </Card>
        <Card>
          <div className="space-y-6">
            <h2 className="text-lg font-semibold mb-4">Active dates</h2>

            <div>
              <div className="grid grid-cols-2 gap-2">
                <FormField
                  label="Start Date"
                  type="date"
                  defaultValue={StartDate?.split('T')[0] || ''}
                  onChange={(e) => {
                    const date = e.target.value;
                    const time = StartDate?.split('T')[1] || '00:00';
                    setValue('startDate', `${date}T${time}`);
                  }}
                  className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                />
                <FormField
                  label="Start Time"
                  type="time"
                  defaultValue={
                    StartDate?.split('T')[1]?.slice(0, 5) || '00:00'
                  }
                  value={
                    StartDate
                      ? StartDate.slice(11, 16) // "04:05"
                      : '00:00'
                  }
                  onChange={(e) => {
                    const time = e.target.value;
                    const date =
                      StartDate?.split('T')[0] ||
                      new Date().toISOString().split('T')[0];
                    setValue('startDate', `${date}T${time}`);
                  }}
                  className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                />
              </div>
            </div>
            <div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <FormField
                  label="End Date"
                  type="date"
                  defaultValue={endDate?.split('T')[0] || ''}
                  onChange={(e) => {
                    const date = e.target.value;
                    const time = endDate?.split('T')[1] || '23:59';
                    setValue('expirationDate', `${date}T${time}`);
                  }}
                  className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                />

                {/* Time */}
                <FormField
                  label="End Time"
                  type="time"
                  defaultValue={endDate?.split('T')[1]?.slice(0, 5) || '23:59'}
                  value={
                    endDate
                      ? endDate.slice(11, 16) // "04:05"
                      : '00:00'
                  }
                  onChange={(e) => {
                    const time = e.target.value;
                    const date =
                      endDate?.split('T')[0] ||
                      new Date().toISOString().split('T')[0];
                    setValue('expirationDate', `${date}T${time}`);
                  }}
                  className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                />
              </div>
              <ErrorMessage message={dateError as string} />
            </div>
          </div>
        </Card>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => {
              (reset(), navigate(RoutesLink.coupons));
            }}
            type="button"
            className="btn-secondary"
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            {isLoading ? <BtnLoader /> : 'Save'}
          </button>
        </div>
      </form>
    </Layout>
  );
};

export default AddProductCoupon;
